import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCategorias } from '../services/publicacionesService';
import { getInstitution, getInstitutionTailwind } from '../config/institutionConfig';

const HomePage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const institution = getInstitution();
  const tw = getInstitutionTailwind();
  const colors = institution.colors;

  useEffect(() => {
    getCategorias(institution.id)
      .then(setCategories)
      .catch(() => setError('No se pudieron cargar las categorías'))
      .finally(() => setLoading(false));
  }, [institution.id]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0" style={{
          background: `linear-gradient(135deg, ${colors.headerGradientStart}, ${colors.headerGradientMid}, ${colors.headerGradientEnd})`
        }} />
        <div className="absolute inset-0 bg-pattern opacity-10" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="flex items-center gap-5">
            <div className="flex-shrink-0">
              <img
                src={`${process.env.PUBLIC_URL}${institution.logo}`}
                alt={`Logo ${institution.shortName}`}
                className="h-14 sm:h-16 drop-shadow-lg"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight drop-shadow-md">
                Centro de Publicaciones
              </h1>
              <p className="mt-1.5 text-white/85 text-sm sm:text-base font-light">
                {institution.description}
              </p>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block">
            <path d="M0 60V30C240 0 480 0 720 30C960 60 1200 60 1440 30V60H0Z" fill="#F9FAFB" />
          </svg>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="mb-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Categorías de Publicaciones
          </h2>
          <p className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto">
            Selecciona una categoría para explorar las publicaciones disponibles
          </p>
        </div>

        {/* Categories Grid */}
        {loading ? (
          <div className="text-center py-16 text-gray-400">Cargando categorías...</div>
        ) : error ? (
          <div className="text-center py-16 text-red-500">{error}</div>
        ) : categories.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center border border-gray-200">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: `${colors.primary}15` }}>
              <svg className="h-8 w-8 flex-shrink-0" fill="none" stroke={colors.primary} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No hay categorías disponibles</h3>
            <p className="text-gray-500">Agrega categorías en el archivo de configuración para comenzar</p>
          </div>
        ) : loading || error ? null : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {categories.map((category, index) => (
              <Link
                key={category.id}
                to={`/${category.slug}`}
                className="group relative bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-gray-300 hover:-translate-y-1"
              >
                {/* Card Header Gradient */}
                <div
                  className="relative h-36 overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${colors.cardGradientFrom}, ${colors.cardGradientVia}, ${colors.cardGradientTo})`
                  }}
                >
                  {/* Decorative circles */}
                  <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10" />
                  <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-full bg-white/10" />

                  {/* Icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center justify-center w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-7 h-7 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                  </div>

                  {/* Badge */}

                </div>

                {/* Card Body */}
                <div className="p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:transition-colors" style={{ '--hover-color': colors.accentFrom }}>
                    {category.nombre}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4 line-clamp-2 leading-relaxed">
                    {category.descripcion}
                  </p>

                  {/* CTA */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="font-semibold text-sm transition-colors" style={{ color: colors.ctaText }}>
                      Explorar publicaciones
                    </span>
                    <span
                      className="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300 group-hover:translate-x-0.5"
                      style={{ backgroundColor: `${colors.primary}10`, color: colors.ctaText }}
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Info section */}
        <div className={`mt-14 bg-gradient-to-r ${tw.infoBg} border ${tw.infoBorder} rounded-2xl p-6 sm:p-8`}>
          <div className="flex items-start gap-4">
            <div
              className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl"
              style={{ background: `linear-gradient(135deg, ${colors.accentFrom}, ${colors.accentTo})` }}
            >
              <svg className="w-5 h-5 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-base font-bold text-gray-900 mb-3">
                ¿Cómo agregar más publicaciones?
              </h4>
              <ol className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start">
                  <span className={`inline-flex items-center justify-center w-5 h-5 ${tw.stepBadgeBg} text-white rounded-full text-xs font-bold mr-2.5 flex-shrink-0 mt-0.5`}>1</span>
                  <span>Sube el PDF a la carpeta correspondiente en el servidor</span>
                </li>
                <li className="flex items-start">
                  <span className={`inline-flex items-center justify-center w-5 h-5 ${tw.stepBadgeBg} text-white rounded-full text-xs font-bold mr-2.5 flex-shrink-0 mt-0.5`}>2</span>
                  <span>Registra la publicación desde el módulo administrador</span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-gray-400 text-sm">
            © {new Date().getFullYear()} {institution.name} — Centro de Publicaciones
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
